import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { DeleteOutline, EmailOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import moment from 'moment';

import { DateFormat } from '../../../../../utils/date';
import useQuickSearch from '../../../../../hooks/useQuickSearch';
import FirebaseContext from '../../../../../context/FirebaseContext';
import OnboardingCoachNotificationsContext from '../../../../context/OnboardingCoachNotificationsContext';
import CoachOnboarding from '../../../../Model/CoachOnboarding';
import CoachHistory, { getOnboardingChecklistByCoach, HistoryEventType } from '../../../../Model/CoachHistory';
import Coach from '../../../../../Model/Coach';
import GenericDataGrid from '../../../../components/GenericDataGrid';
import QuickSearchToolbar from '../../../../components/QuickSearchToolbar';
import LabelCheckbox from '../../../../components/LabelCheckbox';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import useToast from '../../../../hooks/useToast';
import CoachLeadModal from '../CoachLeadModal';
import OnboardingProgressBar from '../OnboardingProgressBar';
import {
  InfoTag,
  AttentionTag,
  ErrorTag,
  SuccessTag,
  TransparentTag,
} from '../../../../../components/Tags';

import {
  CoachName,
  ActionsContainer,
  DataGridContainer,
  ChipContainer,
} from './styles';
import texts from './texts.json';

const NEW_COACH_CUTOFF_DATE = new Date('2025-08-01');

const OnboardingTable = ({
  rows,
  showAllOnboardings,
  handleCheckboxClick,
  onRefresh,
}) => {
  const [leadToDelete, setLeadToDelete] = useState();
  const [selectedLead, setSelectedLead] = useState(null);
  const [onboardingChecklistCounts, setOnboardingChecklistCounts] = useState({});
  const [onboardingChecklistCollection, setOnboardingChecklistCollection] = useState({ docs: [] });
  // Dataset containing coachId, accountCreatedAt, and checklistItemCount from ACCOUNT_CREATED history
  const [isNewCoachArray, setIsNewCoachArray] = useState([]);
  // Store coach data with IS program enrollment status
  const [coachISProgramStatus, setCoachISProgramStatus] = useState({});

  const { showToast } = useToast();
  const { firebase } = useContext(FirebaseContext);
  const { TOTAL_ONBOARDING_TASKS_EXISTING_COACH } = useContext(OnboardingCoachNotificationsContext);

  const isNewCoachAccount = useCallback((accountCreatedAt) => {
    const accountCreatedDate = new Date(accountCreatedAt);

    return accountCreatedDate > NEW_COACH_CUTOFF_DATE;
  }, []);

  // Fetch onboarding history docs and count completed tasks by coach
  useEffect(() => {
    const fetchOnboardingChecklistCounts = async () => {
      if (!rows.length) {
        setOnboardingChecklistCollection({
          docs: [], empty: true, size: 0, metadata: {},
        });
        setOnboardingChecklistCounts({});
        setIsNewCoachArray([]);
        return;
      }

      try {
        // Extract coach IDs from rows
        const coachIDs = rows.map((row) => row.coach).filter(Boolean);

        const CHUNK_SIZE = 10;
        let allCoachChecklistDocs = [];
        let combinedCollection = null;

        if (coachIDs.length > CHUNK_SIZE) {
          // Split coachIDs into chunks of 10
          const chunkPromises = [];
          for (let i = 0; i < coachIDs.length; i += CHUNK_SIZE) {
            const chunk = coachIDs.slice(i, i + CHUNK_SIZE);
            chunkPromises.push(CoachHistory.getHistoryDocsByCoachIds(chunk));
          }

          const chunkResults = await Promise.all(chunkPromises);

          // Flatten all docs
          const combinedDocs = chunkResults.flatMap((chunk) => chunk.docs);
          allCoachChecklistDocs = combinedDocs;

          combinedCollection = {
            docs: combinedDocs,
            empty: combinedDocs.length === 0,
            size: combinedDocs.length,
            metadata: chunkResults[0]?.metadata || {},
          };
        } else {
          // Single request for small datasets
          const coachChecklistCollection = await CoachHistory.getHistoryDocsByCoachIds(coachIDs);
          allCoachChecklistDocs = coachChecklistCollection.docs;
          combinedCollection = coachChecklistCollection;
        }

        // --- Count completed onboarding tasks ---
        // Each coach's checklist depends on when their account was created (the first chat message step only
        // counts for recent accounts), so read the creation dates first.
        const accountCreatedAtByCoach = {};
        allCoachChecklistDocs.forEach(({ coachId, type, createdAt }) => {
          if (coachId && type === HistoryEventType.ACCOUNT_CREATED) {
            accountCreatedAtByCoach[coachId] = createdAt;
          }
        });

        const countsByCoach = {};
        allCoachChecklistDocs.forEach((doc) => {
          const { coachId, type } = doc;
          if (!coachId) return;

          if (getOnboardingChecklistByCoach(accountCreatedAtByCoach[coachId]).includes(type)) {
            countsByCoach[coachId] = (countsByCoach[coachId] || 0) + 1;
          }
        });

        setOnboardingChecklistCollection(combinedCollection);
        setOnboardingChecklistCounts(countsByCoach);
      } catch (error) {
        showToast(format(texts.errorFetchingOnboardingChecklistCounts, { error: error.message }), { error: true });
        setOnboardingChecklistCollection({
          docs: [], empty: true, size: 0, metadata: {},
        });
        setOnboardingChecklistCounts({});
      }
    };

    fetchOnboardingChecklistCounts();
  }, [rows, showToast, onboardingChecklistCollection.docs.length]);

  // Extract ACCOUNT_CREATED docs from onboarding checklist collection
  useEffect(() => {
    if (!onboardingChecklistCollection.docs.length) {
      setIsNewCoachArray([]);
      return;
    }

    const accountCreatedData = onboardingChecklistCollection.docs
      .filter((doc) => doc.type === HistoryEventType.ACCOUNT_CREATED)
      .map((doc) => ({
        coachId: doc.coachId,
        accountCreatedAt: doc.createdAt,
        isNewCoachAccount: isNewCoachAccount(doc.createdAt),
      }));

    setIsNewCoachArray(accountCreatedData);
  }, [onboardingChecklistCollection, isNewCoachAccount]);

  // Fetch coach data to get IS program enrollment status
  // This is needed to adjust the onboarding progress calculation for coaches enrolled in the IS program
  // Coaches in the IS program have one less task to complete (IS AC questionnaire is not required)
  // First check if IS Questionnaire task is completed, then fall back to isEnrolledInISProgram flag
  useEffect(() => {
    const fetchCoachISProgramStatus = async () => {
      if (!isNewCoachArray.length) {
        setCoachISProgramStatus({});
        return;
      }

      try {
        const coachIDs = [...new Set(isNewCoachArray.map((row) => row.coachId).filter(Boolean))];

        const coachPromises = coachIDs.map(async (coachId) => {
          try {
            // Check if IS Questionnaire task has been completed
            const isQuestionnaireCompleted = await CoachHistory.getCoachHistoryByCoachIdAndType(
              coachId,
              HistoryEventType.IS_AC_QUESTIONNAIRE_SUBMITTED,
            );

            // Only fetch coach doc if questionnaire is not completed
            if (!isQuestionnaireCompleted) {
              const coachDoc = await Coach.getCoachById(coachId);
              return {
                coachId,
                isEnrolledInISProgram: coachDoc?.isEnrolledInISProgram || false,
              };
            }

            return {
              coachId,
              isEnrolledInISProgram: false,
            };
          } catch (error) {
            return {
              coachId,
              isEnrolledInISProgram: false,
            };
          }
        });

        const coachResults = await Promise.all(coachPromises);
        const statusMap = coachResults.reduce((acc, { coachId, isEnrolledInISProgram }) => {
          acc[coachId] = isEnrolledInISProgram;
          return acc;
        }, {});

        setCoachISProgramStatus(statusMap);
      } catch (error) {
        showToast(format(texts.errorFetchingCoachData, { error: error.message }), { error: true });
        setCoachISProgramStatus({});
      }
    };

    fetchCoachISProgramStatus();
  }, [isNewCoachArray, showToast]);

  const onResendCoachEmail = useCallback(async (coachEmail) => {
    await firebase.remote('coachAccountCreation/onboardingemail', {
      coachOnboardingId: rows.find(({ email }) => email === coachEmail).id,
    });
    showToast(format(texts.emailSentMessage, { email: coachEmail }));
  }, [
    firebase,
    rows,
    showToast,
  ]);

  const renderNameCell = useCallback(({ row }) => (
    <CoachName onClick={() => setSelectedLead(row)}>{row.name}</CoachName>
  ), []);

  const renderOnboardingProgressCell = useCallback(({ row }) => {
    const coachAccountInfo = isNewCoachArray.find((account) => account.coachId === row.coach);
    if (!coachAccountInfo) {
      return '-';
    }

    // Get IS program enrollment status for this coach
    // This will be used to adjust the total tasks count in the progress bar
    const isEnrolledInISProgram = coachISProgramStatus[row.coach] || false;

    return (
      <OnboardingProgressBar
        onboardingChecklistCount={onboardingChecklistCounts[row.coach]}
        isNewCoachAccount={row.isNewCoach}
        accountCreatedAt={row.accountCreatedAt}
        isEnrolledInISProgram={isEnrolledInISProgram}
      />
    );
  }, [onboardingChecklistCounts, isNewCoachArray, coachISProgramStatus]);

  // The actions cell is rendered differently, based on the status of the row.
  const renderActionCell = useCallback(
    ({
      row: {
        id,
        isOnboarded,
      } = {},
    }) => {
      const onRemove = () => setLeadToDelete(id);
      const onEmail = () => onResendCoachEmail(id);
      return (
        <ActionsContainer>
          <IconButton disabled={isOnboarded} onClick={onRemove}>
            <DeleteOutline />
          </IconButton>
          <IconButton disabled={isOnboarded} onClick={onEmail}>
            <EmailOutlined />
          </IconButton>
        </ActionsContainer>
      );
    },
    [onResendCoachEmail],
  );

  const renderStatusCell = useCallback(
    ({ row }) => {
      const {
        onboardingChecklistCount,
        launchDate,
        isNewCoach,
        accountCreatedAt,
        coach,
      } = row;
      const completed = onboardingChecklistCount || 0;
      // Get IS program enrollment status for this coach
      const isEnrolledInISProgram = coachISProgramStatus[coach] || false;
      // For coaches enrolled in IS program, reduce total tasks by 1 (IS questionnaire is not required)
      // This ensures the launch status calculation is consistent with the progress bar calculation
      let totalTasks;
      if (isNewCoach) {
        // The first chat message step only applies to recent accounts
        const newCoachTaskCount = getOnboardingChecklistByCoach(accountCreatedAt).length;
        totalTasks = isEnrolledInISProgram ? newCoachTaskCount - 1 : newCoachTaskCount;
      } else {
        totalTasks = TOTAL_ONBOARDING_TASKS_EXISTING_COACH;
      }
      const progress = totalTasks > 0 ? Math.round((completed / totalTasks) * 100) : 0;

      if (!launchDate) {
        return (
          <ChipContainer>
            <TransparentTag>{texts.launchStatus.noLaunchDate}</TransparentTag>
          </ChipContainer>
        );
      }

      const now = new Date();
      const daysUntilLaunch = Math.ceil((launchDate - now) / (1000 * 60 * 60 * 24));
      const isOverdue = daysUntilLaunch < 0;
      const isLaunched = progress === 100;

      let StatusTag;
      let statusText;

      if (isLaunched) {
        StatusTag = SuccessTag;
        statusText = texts.launchStatus.launched;
      } else if (isOverdue) {
        StatusTag = AttentionTag;
        statusText = texts.launchStatus.overdue;
      } else if (daysUntilLaunch <= 3) {
        StatusTag = ErrorTag;
        statusText = texts.launchStatus.atRisk;
      } else {
        StatusTag = InfoTag;
        statusText = texts.launchStatus.onTrack;
      }

      return (
        <ChipContainer>
          <StatusTag>{statusText}</StatusTag>
        </ChipContainer>
      );
    },
    [TOTAL_ONBOARDING_TASKS_EXISTING_COACH, coachISProgramStatus],
  );

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: texts.headers.name,
      flex: 15,
      quickSearch: true,
      renderCell: renderNameCell,
    },
    {
      field: 'email',
      headerName: texts.headers.email,
      flex: 20,
    },
    {
      field: 'onboardingProgress',
      headerName: texts.headers.onboardingProgress,
      flex: 20,
      renderCell: renderOnboardingProgressCell,
      sortable: false,
    },
    {
      field: 'launchDate',
      headerName: texts.headers.launchDate,
      type: 'dateTime',
      flex: 10,
      valueGetter: ({ row }) => row.launchDate || null,
      renderCell: ({ row }) => (row.launchDate
        ? moment(row.launchDate).format(DateFormat.DATE_FORMAT_COMMA)
        : '-'),
    },
    {
      field: 'launchStatus',
      headerName: texts.headers.launchStatus,
      flex: 10,
      renderCell: renderStatusCell,
    },
    {
      field: 'status',
      headerName: texts.headers.status,
      flex: 15,
    },
    {
      field: 'coach',
      headerName: texts.headers.coach,
      flex: 20,
      sortable: false,
    },
    {
      field: 'createdAt',
      headerName: texts.headers.dateCreated,
      type: 'dateTime',
      flex: 10,
    },
    {
      field: 'actions',
      headerName: texts.headers.actions,
      flex: 10,
      renderCell: renderActionCell,
      sortable: false,
    },
  ], [
    renderNameCell,
    renderActionCell,
    renderStatusCell,
    renderOnboardingProgressCell,
  ]);

  const preparedRows = useMemo(() => rows.map((row) => {
    const coachAccountInfo = isNewCoachArray.find((account) => account.coachId === row.coach);
    const isNewCoach = coachAccountInfo ? coachAccountInfo.isNewCoachAccount : false;
    const accountCreatedAt = coachAccountInfo?.accountCreatedAt;

    return {
      onboardingId: row.id,
      id: row.email,
      name: row.name,
      email: row.email,
      status: row.status,
      coach: row.coach,
      launchDate: row.launchDate,
      createdAt: row.createdAt,
      coachDriveFolder: row.coachDriveFolder,
      onboardingChecklistCount: onboardingChecklistCounts[row.coach] || 0,
      isNewCoach,
      accountCreatedAt,
      onboardingDocument: row,
    };
  }), [rows, onboardingChecklistCounts, isNewCoachArray]);

  const handleLeadDelete = useCallback(async () => {
    await CoachOnboarding.removeCoachOnboardingDoc(rows.find(({ email }) => email === leadToDelete).id);
    showToast(format(texts.leadDeleteMessage, { leadName: leadToDelete }));
    setLeadToDelete(null);
  }, [
    leadToDelete,
    rows,
    showToast,
  ]);

  const {
    filteredRows: quickSearchRows,
    toolbarProps,
  } = useQuickSearch(preparedRows, columns);

  return (
    <DataGridContainer>
      <GenericDataGrid
        rows={quickSearchRows}
        columns={columns}
        disableSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: 'createdAt', sort: 'desc' }],
          },
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
        }}
        components={{
          Toolbar: QuickSearchToolbar,
        }}
        componentsProps={{
          toolbar: {
            ...toolbarProps,
            placeholder: texts.searchPlaceholder,
            filterTools: [
              {
                Component: LabelCheckbox,
                id: 'crx-filter',
                props: {
                  isChecked: showAllOnboardings,
                  description: texts.showAll,
                  onChange: handleCheckboxClick,
                },
              },
            ],
          },
        }}
      />
      <ConfirmDialog
        isOpen={!!leadToDelete}
        onConfirm={handleLeadDelete}
        onCancel={() => setLeadToDelete(null)}
        dialogTexts={{
          title: texts.deleteConfirm,
          content: leadToDelete || '',
        }}
      />
      {!!selectedLead && (
        <CoachLeadModal
          open={!!selectedLead}
          onClose={() => setSelectedLead(null)}
          onboardingDoc={selectedLead}
          onRefresh={onRefresh}
          isEnrolledInISProgram={coachISProgramStatus[selectedLead.coach] || false}
        />
      )}
    </DataGridContainer>
  );
};

OnboardingTable.propTypes = {
  rows: PropTypes.array.isRequired,
  showAllOnboardings: PropTypes.bool.isRequired,
  handleCheckboxClick: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
};

OnboardingTable.defaultProps = {
  onRefresh: () => {},
};

export default compose(
  observer,
)(OnboardingTable);
